// --------------- Types ---------------

export interface Scan {
  id: string;
  target: string;
  mode: string;
  status: string;
  findings_count: number;
  created_at: string;
}

export interface Finding {
  id: string;
  title: string;
  severity: string;
  cvss_score: number;
  endpoint: string;
  scan_id?: string;
  description?: string;
  created_at?: string;
}

export interface Project {
  id: string;
  name: string;
  description: string;
  scans_count?: number;
  findings_count?: number;
  created_at?: string;
}

export interface ScanCreateRequest {
  target: string;
  mode: string;
  project_id?: string;
}

export interface AuthResponse {
  access_token: string;
  token_type: string;
  username: string;
  user_id: string;
}

// --------------- Token storage ---------------

const TOKEN_KEY = "petir.token";

export function getToken(): string | null {
  return localStorage.getItem(TOKEN_KEY);
}

export function setToken(token: string): void {
  localStorage.setItem(TOKEN_KEY, token);
}

export function clearToken(): void {
  localStorage.removeItem(TOKEN_KEY);
}

// --------------- Client ---------------

const API_BASE = import.meta.env.VITE_API_URL || "/api";

async function request<T>(path: string, options?: RequestInit): Promise<T> {
  const token = getToken();
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
    ...((options?.headers as Record<string, string> | undefined) ?? {}),
  };
  if (token) headers["Authorization"] = `Bearer ${token}`;

  const res = await fetch(`${API_BASE}${path}`, { ...options, headers });

  if (res.status === 401) {
    clearToken();
    window.dispatchEvent(new Event("petir:unauthorized"));
  }

  if (!res.ok) {
    throw new Error(await readError(res));
  }

  if (res.status === 204) return undefined as T;
  return res.json();
}

async function readError(res: Response): Promise<string> {
  const body = await res.text().catch(() => "");
  try {
    const parsed = JSON.parse(body);
    if (parsed?.detail) {
      return typeof parsed.detail === "string" ? parsed.detail : JSON.stringify(parsed.detail);
    }
  } catch {
    /* fall through to raw body */
  }
  return `${res.status} ${res.statusText}${body ? " — " + body : ""}`;
}

// --------------- Auth ---------------

export function login(username: string, password: string): Promise<AuthResponse> {
  return request<AuthResponse>("/auth/login", {
    method: "POST",
    body: JSON.stringify({ username, password }),
  });
}

// --------------- API functions ---------------

export function fetchProjects(): Promise<Project[]> {
  return request<Project[]>("/projects");
}

export function createProject(data: { name: string; description?: string }): Promise<Project> {
  return request<Project>("/projects", { method: "POST", body: JSON.stringify(data) });
}

export function fetchProject(id: string): Promise<Project> {
  return request<Project>(`/projects/${id}`);
}

export function fetchScans(): Promise<Scan[]> {
  return request<Scan[]>("/scans");
}

export function createScan(data: ScanCreateRequest): Promise<Scan> {
  return request<Scan>("/scans", { method: "POST", body: JSON.stringify(data) });
}

export function fetchScan(id: string): Promise<Scan> {
  return request<Scan>(`/scans/${id}`);
}

export function fetchFindings(scanId?: string): Promise<Finding[]> {
  return request<Finding[]>(scanId ? `/scans/${scanId}/findings` : "/findings");
}
